import type { Datasource } from '@/types'
import type {
  RecordStrategy,
  RecordStrategyArgs,
  SearchableData,
} from '../types'
import { DEFAULT_PAGINATION } from '@/constants/pagination'
import { useDatasources } from '@/store/modules/App/datasource'
import { storeToRefs } from 'pinia'

export class DatasourcesRecord implements RecordStrategy {
  async set(args: RecordStrategyArgs = {}): Promise<void> {
    const { spaceId } = args
    if (!spaceId) {
      return
    }

    await this.loadDatasources(spaceId)
    const datasourcesStore = useDatasources()
    const { datasources } = storeToRefs(datasourcesStore)

    window?.CommandBar?.addRecords(
      'datasources',
      this.createSearchableRecord(datasources.value ?? [], spaceId),
      {
        onInputChange: (searchTerm: string) =>
          this.onSearchDatasources(spaceId, searchTerm),
        searchTabEnabled: true,
      },
    )
  }

  async onSearchDatasources(
    spaceId: string,
    searchTerm: string,
  ): Promise<SearchableData[]> {
    const datasourcesStore = useDatasources()
    const { datasources } = storeToRefs(datasourcesStore)

    let results = datasources.value?.filter((datasource) =>
      datasource.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    if (!results?.length) {
      results = await this.loadDatasources(spaceId, searchTerm)
    }

    return this.createSearchableRecord(results, spaceId)
  }

  async loadDatasources(
    spaceId: string,
    searchTerm: string = '',
  ): Promise<Datasource[]> {
    if (!spaceId) {
      return []
    }
    try {
      const data = await useDatasources().loadDatasources({
        spaceId: spaceId,
        params: {
          page: DEFAULT_PAGINATION.currentPage,
          per_page: 50,
          search: searchTerm,
          sort_by: 'name:asc',
        },
      })

      return data.datasources
    } catch (error) {
      console.error('Error fetching datasources', error)
      return []
    }
  }

  createSearchableRecord(
    records: Datasource[],
    spaceId: string,
  ): SearchableData[] {
    return records.map((item: Datasource) => ({
      id: item.id,
      title: item.name,
      label: item.name,
      url: `/#/me/spaces/${spaceId}/datasources/${item.id}`,
    }))
  }

  remove(): void {
    window?.CommandBar?.addRecords('datasources', [], {
      searchTabEnabled: false,
    })
  }
}
